'use client';

import { c as _c } from "react/compiler-runtime";
import { getIndustryProfile } from '@lib/onboarding/constants/industry-profiles';
import { cn } from 'cn';
import { Check } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { GOAL_PREVIEWS } from '@/app/onboarding/_components/workspace-setup/previews/goal-previews';
import { DEFAULT_ACCENT } from '@/app/onboarding/_components/workspace-setup/previews/preview-frame';
import { ActivityFeedPreview } from './previews/activity-feed-preview';
import { AskClaiPreview } from './previews/ask-clai-preview';
import { ClaiHubPreview } from './previews/clai-hub-preview';
import type { AuthPanelSlide, AuthSlideVisual } from './types/auth-panel-deck';

/** Previews use real-estate documents and deals. */
const AGENT_PROFILE = getIndustryProfile('Residential real estate');
const CHIP_POSITIONS = ['-top-4 -left-6', '-right-6 -bottom-4'] as const;
function Preview(t0) {
  const $ = _c(5);
  const {
    visual
  } = t0;
  switch (visual) {
    case "claiHub":
      {
        let t1;
        if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <ClaiHubPreview />;
          $[0] = t1;
        } else {
          t1 = $[0];
        }
        return t1;
      }
    case "activityFeed":
      {
        let t1;
        if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <ActivityFeedPreview />;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    case "askClai":
      {
        let t1;
        if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <AskClaiPreview />;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        return t1;
      }
    default:
      {
        const GoalPreview = GOAL_PREVIEWS[visual];
        let t1;
        if ($[3] !== GoalPreview) {
          t1 = <GoalPreview accent={DEFAULT_ACCENT} name="" profile={AGENT_PROFILE} />;
          $[3] = GoalPreview;
          $[4] = t1;
        } else {
          t1 = $[4];
        }
        return t1;
      }
  }
}

/**
 * The slide's mock preview, with its chips popping in
 * around it. Hidden from screen readers: it is
 * illustration, and the caption already says what the slide
 * shows.
 */
export function SlideVisual(t0) {
  const $ = _c(8);
  const {
    slide
  } = t0;
  const reduceMotion = useReducedMotion();
  let t1;
  if ($[0] !== slide.visual) {
    t1 = <Preview visual={slide.visual} data-sentry-element="Preview" data-sentry-source-file="slide-visual.tsx" />;
    $[0] = slide.visual;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== reduceMotion || $[3] !== slide.chips) {
    t2 = slide.chips?.map((chip, i) => <motion.div key={chip} className={cn("absolute z-10 hidden lg:block", CHIP_POSITIONS[i] ?? CHIP_POSITIONS[0])} initial={reduceMotion ? false : {
      opacity: 0,
      scale: 0.6,
      y: 12
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      type: "spring",
      stiffness: 260,
      damping: 18,
      delay: 0.9 + i * 0.6
    }}><motion.div className="border-border bg-card/90 text-foreground flex items-center gap-1.5 rounded-full border py-1.5 pr-3 pl-1.5 text-xs font-medium shadow-lg backdrop-blur-sm" animate={reduceMotion ? undefined : {
        y: [0, -5, 0]
      }} transition={{
        duration: 3.2,
        delay: i * 0.8,
        repeat: Number.POSITIVE_INFINITY,
        ease: "easeInOut"
      }}><span className="bg-primary text-primary-foreground flex size-5 items-center justify-center rounded-full"><Check className="size-3" /></span>{chip}</motion.div></motion.div>);
    $[2] = reduceMotion;
    $[3] = slide.chips;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div aria-hidden={true} className="relative w-full max-w-md" data-sentry-component="SlideVisual" data-sentry-source-file="slide-visual.tsx">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}